import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import { X, Loader2, Upload, MapPin, Camera } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * iter109 — Submit-a-walk modal.
 *
 * Logged-in users contribute their own local walks. Walk submissions go
 * into `user_walks` with status='pending' and surface on the campsite
 * detail page once an admin approves them.
 *
 * The location picker is a Leaflet map seeded at the campsite's coords.
 * Tap anywhere to drop a pin — no geolocation prompt (privacy-friendly,
 * works on desktop too).
 */

const markerIcon = L.divIcon({
  className: "",
  html: `<div class="cc-marker" style="background:#0a7f3f;color:#fff;">●</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

function MapPicker({ initial, onPick }) {
  const [pos, setPos] = useState(initial);
  useEffect(() => { onPick(pos); /* eslint-disable-next-line */ }, [pos]);
  function Tap() {
    useMapEvents({
      click(e) { setPos([e.latlng.lat, e.latlng.lng]); },
    });
    return null;
  }
  return (
    <MapContainer
      center={initial}
      zoom={12}
      style={{ height: "240px", width: "100%" }}
      className="border hairline"
      scrollWheelZoom={false}
      data-testid="submit-walk-map"
    >
      <TileLayer
        attribution='Map by Leaflet · &copy; OpenStreetMap'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Tap />
      {pos && <Marker position={pos} icon={markerIcon} />}
    </MapContainer>
  );
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("File read failed"));
    r.readAsDataURL(file);
  });
}

export default function SubmitWalkModal({ campsiteId, campsiteName, campsiteLat, campsiteLng, onClose, onSubmitted }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [lengthKm, setLengthKm] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [incline, setIncline] = useState("");
  const [familyFriendly, setFamilyFriendly] = useState(false);
  const [childFriendly, setChildFriendly] = useState(false);
  const [dogFriendly, setDogFriendly] = useState(false);
  const [pickedPos, setPickedPos] = useState([campsiteLat, campsiteLng]);
  const [photos, setPhotos] = useState([]); // array of {image_data_url, caption}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    const slotsLeft = 3 - photos.length;
    const next = [];
    for (const f of files.slice(0, slotsLeft)) {
      if (f.size > 2_000_000) {
        toast.error(`"${f.name}" is over 2 MB — please pick a smaller image.`);
        continue;
      }
      const url = await fileToDataUrl(f);
      next.push({ image_data_url: url, caption: "" });
    }
    setPhotos((p) => [...p, ...next]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (description.length < 20) { toast.error("Description must be at least 20 characters."); return; }
    if (!pickedPos) { toast.error("Tap on the map to pick the walk's start point."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/campsites/${campsiteId}/walks/submit`, {
        name: name.trim(),
        description: description.trim(),
        lat: pickedPos[0],
        lng: pickedPos[1],
        length_km: lengthKm ? Number(lengthKm) : null,
        difficulty: difficulty || null,
        incline: incline || null,
        family_friendly: familyFriendly,
        child_friendly: childFriendly,
        dog_friendly: dogFriendly,
        photos,
      });
      toast.success(data.message || "Thanks — submitted for review!");
      onSubmitted && onSubmitted(data);
      onClose && onClose();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="submit-walk-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white w-full sm:max-w-2xl border-2 border-[#111] shadow-[0_6px_0_0_#ff4500] max-h-[95vh] overflow-y-auto"
      >
        <div className="p-5 border-b hairline flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="eyebrow !text-[#0a7f3f]">Crowdsource a walk</div>
            <h3 className="font-display text-xl mt-1">Submit a walk near {campsiteName || "this campsite"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#fafafa]" data-testid="submit-walk-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="eyebrow block mb-1">Walk name *</label>
            <input
              type="text"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. River Mole loop"
              className="w-full border hairline px-3 py-2 text-sm focus:outline-none focus:border-[#0a7f3f]"
              data-testid="submit-walk-name"
            />
          </div>

          <div>
            <label className="eyebrow block mb-1">What to expect *</label>
            <textarea
              required
              minLength={20}
              maxLength={1500}
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the route — terrain, scenery, parking, dog-friendliness, etc. (min 20 chars)"
              className="w-full border hairline px-3 py-2 text-sm focus:outline-none focus:border-[#0a7f3f]"
              data-testid="submit-walk-description"
            />
            <div className="text-[10px] text-[#888] mt-1">{description.length}/1500</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Length (km)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="200"
                value={lengthKm}
                onChange={(e) => setLengthKm(e.target.value)}
                placeholder="e.g. 4.5"
                className="w-full border hairline px-3 py-2 text-sm focus:outline-none focus:border-[#0a7f3f]"
                data-testid="submit-walk-length"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Difficulty</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                className="w-full border hairline px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0a7f3f]"
                data-testid="submit-walk-difficulty"
              >
                <option value="">— pick one —</option>
                <option value="Easy">Easy</option>
                <option value="Moderate">Moderate</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1">Incline</label>
            <select
              value={incline}
              onChange={(e) => setIncline(e.target.value)}
              className="w-full border hairline px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0a7f3f]"
              data-testid="submit-walk-incline"
            >
              <option value="">— pick one —</option>
              <option value="Flat">Flat (no real climb)</option>
              <option value="Gentle">Gentle (mild slopes)</option>
              <option value="Moderate">Moderate (some climbs)</option>
              <option value="Steep">Steep (serious ascent)</option>
            </select>
          </div>

          <fieldset className="border hairline p-3">
            <legend className="eyebrow px-1">Great for</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="submit-walk-family-label">
                <input
                  type="checkbox"
                  checked={familyFriendly}
                  onChange={(e) => setFamilyFriendly(e.target.checked)}
                  className="accent-[#0a7f3f]"
                  data-testid="submit-walk-family"
                />
                👨‍👩‍👧 Families
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="submit-walk-child-label">
                <input
                  type="checkbox"
                  checked={childFriendly}
                  onChange={(e) => setChildFriendly(e.target.checked)}
                  className="accent-[#0a7f3f]"
                  data-testid="submit-walk-child"
                />
                👶 Young children
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="submit-walk-dog-label">
                <input
                  type="checkbox"
                  checked={dogFriendly}
                  onChange={(e) => setDogFriendly(e.target.checked)}
                  className="accent-[#0a7f3f]"
                  data-testid="submit-walk-dog"
                />
                🐕 Dogs
              </label>
            </div>
            <p className="text-[10px] text-[#888] mt-2">
              Tick all that apply — helps other campers filter walks that suit them.
            </p>
          </fieldset>

          <div>
            <label className="eyebrow block mb-1 flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-[#0a7f3f]" /> Tap on the map to pin the start
            </label>
            <MapPicker initial={[campsiteLat, campsiteLng]} onPick={setPickedPos} />
            <div className="text-[10px] mono text-[#666] mt-1">
              {pickedPos ? `Pinned: ${pickedPos[0].toFixed(4)}, ${pickedPos[1].toFixed(4)}` : "No pin yet"}
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-1 flex items-center gap-1.5">
              <Camera className="w-3 h-3" /> Photos (up to 3, optional)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              onChange={addPhoto}
              className="text-[12px] hidden"
              id="walk-photos-input"
              data-testid="submit-walk-photos"
              disabled={photos.length >= 3}
            />
            <label
              htmlFor="walk-photos-input"
              className={`inline-flex items-center gap-1.5 border hairline px-3 py-1.5 text-[12px] cursor-pointer hover:bg-[#fafafa] ${photos.length >= 3 ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Upload className="w-3 h-3" /> Add photo
            </label>
            {photos.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative border hairline bg-white" data-testid={`submit-walk-photo-${i}`}>
                    <img src={p.image_data_url} alt="walk preview" className="w-full h-20 object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 bg-white/90 border hairline p-0.5"
                      aria-label="Remove"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-5 border-t hairline sticky bottom-0 bg-white flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-3 text-xs">Cancel</button>
          <button
            type="submit"
            disabled={busy}
            className="bg-[#0a7f3f] text-white text-xs uppercase tracking-wider font-bold py-2.5 px-4 shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform disabled:opacity-50"
            data-testid="submit-walk-submit"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null}
            Submit for review
          </button>
        </div>
      </form>
    </div>
  );
}
